<template>
  <div class="modal-overlay">
    <div class="modal-container modal-full-width">
      <!-- Cabeçalho -->
      <div class="modal-header">
        <h6>Trocar Nível / Turma</h6>
        <i class="bi bi-x-circle close-icon" @click="cancel"></i>
      </div>

      <div class="spacer"></div>

      <!-- Dados do Plano Atual (somente leitura) -->
      <div class="modal-content">
        <h5 class="section-title">Dados do Nível Atual</h5>

        <div class="row">
          <div class="col-md-8">
            <div class="form-group">
              <label>Nível Atual:</label>
              <input
                type="text"
                :value="getNivelDescricao(matricula.nivel_id)"
                class="form-control"
                disabled
              />
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group">
              <label>Aulas por Semana:</label>
              <input
                type="text"
                :value="plano.aulas_semana"
                class="form-control form-control-sm"
                disabled
              />
            </div>
          </div>

          <div class="row">
            <div class="col" v-if="matricula.turmas && matricula.turmas.length">
              <div class="form-group">
                <label>Turmas Atuais:</label>
                <ul>
                  <li v-for="(turma, index) in matricula.turmas" :key="index">
                    {{ turma.turma_completa }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dados do Novo Nível -->
      <div class="modal-content">
        <h5 class="section-title">Dados do Novo Nível</h5>

        <!-- Seleção do Nível base -->
        <div class="row">
          <div class="col-md-8">
            <div class="form-group">
              <label>Novo Nível (Base): <span class="required">*</span></label>
              <select v-model="novo.nivel_id" class="form-control" @change="handleNovoNivelChange">
                <option value="">Selecione</option>
                <option v-for="nivel in niveis" :key="nivel.id" :value="nivel.id">
                  {{ nivel.nivel }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Turmas (com nível por linha) -->
        <div class="row" v-if="novo.nivel_id">
          <div class="col-md-12">
            <div class="form-group">
              <label>Turmas da Semana: <span class="required">*</span></label>

              <div class="table-responsive mt-2">
                <table class="table table-borderless">
                  <thead>
                    <tr>
                      <th style="width: 220px;">Nível</th>
                      <th>Turma</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(linha, index) in novo.turmas" :key="`linha-${index}`">
                      <td>
                        <select
                          v-model="linha.nivel_id"
                          class="form-control"
                          @change="onNivelLinhaChange(index)"
                        >
                          <option :value="null" disabled>Selecione</option>
                          <option v-for="nivel in niveis" :key="nivel.id" :value="nivel.id">
                            {{ nivel.nivel }}
                          </option>
                        </select>
                      </td>

                      <td>
                        <select
                          v-model="linha.turma_id"
                          class="form-control"
                          @change="atualizarTurma(index)"
                        >
                          <option value="" disabled>Selecione</option>
                          <option v-if="linha.turma_id && !turmaDisponivel(linha.turma_id, turmasDisponiveisPorNivel(linha.nivel_id || novo.nivel_id))" :value="linha.turma_id" disabled>Turma indisponível — selecione uma turma ativa</option>
                          <option
                            v-for="t in turmasDisponiveisPorNivel(linha.nivel_id || novo.nivel_id)"
                            :key="`${t.id}-${t.dia}-${t.horario}`"
                            :value="`${t.id}-${t.dia}-${t.horario}`"
                          >
                            {{ `Turma: ${t.id} | Dia: ${t.dia} | Hora: ${t.horario_fmt} | Instrutor: ${t.instrutor}` }}
                          </option>
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <small class="text-muted d-block mt-2">
                Dica: você pode escolher níveis diferentes por linha (ex.: Nível II e Nível II - Individual).
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Rodapé -->
      <div class="modal-footer">
        <button class="btn btn-secondary me-2" @click="cancel">Cancelar</button>

        <button
          :class="['btn', isSaving ? 'btn-success' : 'btn-primary', 'me-2']"
          :disabled="!formValid || isSaving"
          @click="trocarNivel"
        >
          {{ isSaving ? 'Salvando…' : 'Salvar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { turmaDisponivel, mensagemTurmaIndisponivel } from '@/utils/turmasSelecao';
import { reactive, ref, computed, onMounted, getCurrentInstance } from 'vue';
import axios from '@/services/axios';

export default {
  name: 'TrocarNivel',
  props: {
    matricula: { type: Object, required: true },
    plano: { type: Object, required: true },
    supervisaoId: { type: [Number, String], default: null }
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    if (!instance) throw new Error("getCurrentInstance retornou null.");

    const niveis = ref([]);
    const todasTurmasDisponiveis = ref([]);
    const isSaving = ref(false);

    const novo = reactive({
      nivel_id: '',
      nivel_nome: '',
      turmas: []
    });

    // ========= helpers (mesma ideia do FormularioMatricula) =========
    const idxDia = (d) => {
      let s = String(d || '')
        .normalize('NFD').replace(/\p{Diacritic}/gu, '')
        .toLowerCase().replace(/-?feira/g, '').trim();
      s = s.split(/\s+/)[0].slice(0, 3);
      return { seg: 1, ter: 2, qua: 3, qui: 4, sex: 5, sab: 6, dom: 7 }[s] ?? 99;
    };

    const toMin = (h) => {
      const m = String(h || '').match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (!m) return Number.MAX_SAFE_INTEGER;
      return (+m[1]) * 60 + (+m[2]);
    };

    const formatHora = (h) => {
      const m = String(h || '').match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(h || '');
    };

    const keyDiaHora = (dia, horario) => `${idxDia(dia)}__${String(horario || '').slice(0, 5)}`;

    // conflito imediato: não pode repetir mesmo dia/horário (independente do nível)
    const hasChoqueComOutraLinha = (dia, horario, indexAtual) => {
      const k = keyDiaHora(dia, horario);
      return novo.turmas.some((t, i) => {
        if (i === indexAtual) return false;
        if (!t || !t.dia || !t.horario) return false;
        return keyDiaHora(t.dia, t.horario) === k;
      });
    };

    // ========= carregamentos =========
    const carregarNiveis = async () => {
      try {
        const { data } = await axios.get('/cadastros/niveis-turmas', { params: { page: 1, limit: 10000 } });
        const lista = data?.data ?? data ?? [];
        niveis.value = Array.isArray(lista) ? lista.filter(n => n.ativo) : [];
        niveis.value.sort((a, b) =>
          String(a.nivel).localeCompare(String(b.nivel), 'pt-BR', { sensitivity: 'base', numeric: true })
        );
      } catch (e) {
        console.error('❌ Erro ao carregar níveis:', e);
        niveis.value = [];
      }
    };

    const carregarTodasTurmas = async () => {
      try {
        const res = await axios.get('/cadastros/dias-horarios-turmas', { params: { ativo: true } });
        const linhas = res.data?.data || res.data || [];

        // mapa: nome do nível -> id (niveis já carregado)
        const nivelNomeToId = new Map((niveis.value || []).map(n => [String(n.nivel).trim(), Number(n.id)]));

        todasTurmasDisponiveis.value = (Array.isArray(linhas) ? linhas : [])
          .map(t => {
            const nivelNome = t.nivel_nome || t.nivel || '';
            const nivelId = Number(t.nivel_id ?? nivelNomeToId.get(String(nivelNome).trim()) ?? null);

            // se não conseguirmos resolver o nivel_id, descarta
            if (!Number.isFinite(nivelId)) return null;

            return {
              id: t.turma_id ?? t.id,
              dia: t.dia_semana ?? t.dia,
              horario: t.horario,
              horario_fmt: formatHora(t.horario),
              instrutor: t.instrutor_nome || 'Não informado',
              nivel: nivelNome,
              nivel_id: nivelId,
              label: `${t.turma_id ?? t.id} | ${t.instrutor_nome || 'Não informado'} | ${(t.dia_norm || t.dia_semana || t.dia)} | ${formatHora(t.horario)}`
            };
          })
          .filter(Boolean);
      } catch (e) {
        console.error('❌ Erro ao carregar turmas (base completa):', e);
        todasTurmasDisponiveis.value = [];
      }
    };

    function turmasDisponiveisPorNivel(nivelId) {
      const nId = Number(nivelId);
      const base = (todasTurmasDisponiveis.value || []).filter(t => Number(t.nivel_id) === nId);
      return base.sort((a, b) =>
        (Number(a.id) - Number(b.id)) ||
        (idxDia(a.dia) - idxDia(b.dia)) ||
        (toMin(a.horario) - toMin(b.horario))
      );
    }

    // ========= fluxo de seleção =========
    const handleNovoNivelChange = async () => {
      const sel = niveis.value.find(n => String(n.id) === String(novo.nivel_id));
      novo.nivel_nome = sel ? sel.nivel : '';

      // garante slots exatamente = aulas/semana e já com nível padrão
      const qtd = Number(props.plano.aulas_semana) || 0;
      const nivelPadrao = Number(novo.nivel_id) || null;

      novo.turmas = Array.from({ length: qtd }, () => ({
        nivel_id: nivelPadrao,
        turma_id: "",
        id: null,
        dia: "",
        horario: "",
        instrutor: "",
        turma_completa: ""
      }));
    };

    const onNivelLinhaChange = (index) => {
      const linha = novo.turmas[index];
      if (!linha) return;

      linha.turma_id = "";
      linha.id = null;
      linha.dia = "";
      linha.horario = "";
      linha.instrutor = "";
      linha.turma_completa = "";
    };

    const atualizarTurma = (index) => {
      const linha = novo.turmas[index];
      if (!linha) return;

      const turmaSelecionada = linha.turma_id;
      if (!turmaSelecionada) return;

      // Exigir nivel_id antes (boa UX)
      if (!linha.nivel_id) {
        alert('Selecione o nível desta linha antes de escolher a turma.');
        linha.turma_id = "";
        return;
      }

      // Parse do value "ID-DIA-HH:MM:SS"
      const id = String(turmaSelecionada).split('-')[0];
      const resto = String(turmaSelecionada).substring(String(turmaSelecionada).indexOf('-') + 1);
      const lastHyphen = resto.lastIndexOf('-');
      if (lastHyphen === -1) {
        console.warn('⚠️ Formato inesperado:', turmaSelecionada);
        linha.turma_id = "";
        return;
      }
      const dia = resto.substring(0, lastHyphen);
      const horario = resto.substring(lastHyphen + 1);

      const base = turmasDisponiveisPorNivel(linha.nivel_id || novo.nivel_id);
      const turmaEncontrada = base.find(
        t => String(t.id) === String(id) && t.dia === dia && t.horario === horario
      );

      if (!turmaEncontrada) {
        console.warn("⚠️ Turma não encontrada:", { id, dia, horario, nivel_id: linha.nivel_id });
        linha.turma_id = "";
        return;
      }

      // valida conflito de dia/horário com outras linhas
      if (hasChoqueComOutraLinha(turmaEncontrada.dia, turmaEncontrada.horario, index)) {
        alert(`Conflito: já existe turma em "${turmaEncontrada.dia}" às "${String(turmaEncontrada.horario).slice(0,5)}" em outra linha.`);
        linha.turma_id = "";
        return;
      }

      // Preenche linha
      novo.turmas[index] = {
        ...turmaEncontrada,
        nivel_id: Number(linha.nivel_id) || Number(novo.nivel_id) || null,
        turma_id: turmaSelecionada,
        turma_completa: `Turma: ${turmaEncontrada.id} | Dia: ${turmaEncontrada.dia} | Hora: ${turmaEncontrada.horario} | Instrutor: ${turmaEncontrada.instrutor}`
      };
    };

    // ========= validações =========
    const formValid = computed(() => {
      const nivelValido = !!novo.nivel_id;

      const qtd = Number(props.plano.aulas_semana) || 0;
      const turmasValidas =
        Array.isArray(novo.turmas) &&
        novo.turmas.length === qtd &&
        novo.turmas.every(t =>
          t &&
          t.nivel_id &&
          t.turma_id &&
          String(t.turma_id).trim() !== '' &&
          t.dia &&
          t.horario
        );

      // valida choque final
      if (!turmasValidas) return false;

      const seen = new Set();
      for (let i = 0; i < novo.turmas.length; i++) {
        const t = novo.turmas[i];
        const k = keyDiaHora(t.dia, t.horario);
        if (seen.has(k)) return false;
        seen.add(k);
      }

      return nivelValido && turmasValidas;
    });

    // ========= util =========
    const getNivelDescricao = (nivelId) => {
      const n = (niveis.value || []).find(x => String(x.id) === String(nivelId));
      return n ? n.nivel : 'Não informado';
    };

    // ========= ação =========
    const trocarNivel = async () => {
      if (isSaving.value) return;
      if (novo.turmas.some(t => !turmaDisponivel(t.turma_id, turmasDisponiveisPorNivel(t.nivel_id || novo.nivel_id)))) { alert(mensagemTurmaIndisponivel); return; }
      isSaving.value = true;

      try {
        const userName = instance.appContext.config.globalProperties.$globalState.userName;
        const currentNivelNome = getNivelDescricao(props.matricula.nivel_id);

        const payload = {
          id_matricula: props.matricula.id,
          novo_nivel: novo.nivel_nome,     // nome do novo nível base
          novo_nivel_id: novo.nivel_id,    // id do novo nível base
          nivel_anterior: currentNivelNome,

          // ✅ novas turmas agora incluem nivel_id por linha
          novas_turmas: (novo.turmas || []).map(t => ({
            nivel_id: Number(t.nivel_id) || Number(novo.nivel_id) || null,
            turma_id: Number(String(t.turma_id).split('-')[0]),
            dia: t.dia,
            horario: t.horario,
            turma_completa: t.turma_completa
          })),

          current_nivel: currentNivelNome,
          current_turmas: props.matricula.turmas?.map(t => t.turma_completa) ?? [],
          nome_usuario: userName,
          numero_contrato: props.matricula.numero_contrato,
          supervisao_id: props.supervisaoId || null
        };

        const response = await axios.post('/operacional/matriculas/matriculas-trocas-niveis', payload);

        emit('salvou', response.data);
        cancel();
      } catch (error) {
        console.error('❌ Erro ao trocar nível:', error);
        alert(error.response?.data?.error || 'Erro ao trocar nível. Verifique os logs para mais detalhes.');
      } finally {
        isSaving.value = false;
      }
    };

    const cancel = () => emit('cancelar');

    onMounted(async () => {
      await carregarNiveis();
      await carregarTodasTurmas();
    });

    return {
      niveis,
      novo,
      isSaving,

      handleNovoNivelChange,
      onNivelLinhaChange,
      atualizarTurma,

      turmasDisponiveisPorNivel,
      turmaDisponivel,
      formValid,

      trocarNivel,
      cancel,
      getNivelDescricao
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 600px;
  height: auto;
  overflow-y: auto;
  margin: 0 auto;
  border-radius: 0px;
}

.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

.spacer {
  margin-bottom: 20px;
}

.modal-content {
  padding: 10px 15px;
}

.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.form-group {
  width: 100%;
  margin-bottom: 10px;
}

.form-group label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.form-control {
  width: 100%;
  max-width: 100%;
  padding: 5px;
  border: 1px solid #ced4da;
  border-radius: 0px;
}

.required {
  color: red;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  padding-bottom: 20px;
}

.modal-footer .btn {
  min-width: 70px;
  padding: 4px 15px;
  font-size: 12px;
}

.btn-secondary {
  background-color: #6c757d;
  border: none;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary {
  background-color: #007bff;
  border: none;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary:disabled {
  background-color: #aaa;
  cursor: not-allowed;
}
</style>